import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2, Send, Mail, Phone, MapPin } from 'lucide-react';
import { submitContact } from '../lib/api';

const ContactPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Get quiz data from URL params
  const quizScore = searchParams.get('score');
  const quizLevel = searchParams.get('level');
  const quizSector = searchParams.get('sector');

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    sector: quizSector || '',
    message: '',
    contact_preference: 'email',
    rgpd_consent: false,
    quiz_score: quizScore ? parseInt(quizScore) : null,
    quiz_level: quizLevel || null,
  });

  const sectors = [
    { value: 'artisans', label: 'Artisans & BTP' },
    { value: 'restaurateurs', label: 'Restaurateurs & Hôteliers' },
    { value: 'liberal', label: 'Professions libérales' },
    { value: 'services', label: 'Entreprises de services' },
    { value: 'autre', label: 'Autre' },
  ];

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) newErrors.first_name = 'Prénom requis';
    if (!formData.last_name.trim()) newErrors.last_name = 'Nom requis';
    if (!formData.email.trim()) {
      newErrors.email = 'Email requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    if (!formData.message.trim()) newErrors.message = 'Message requis';
    if (!formData.rgpd_consent) newErrors.rgpd_consent = 'Veuillez accepter les conditions';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      await submitContact(formData);
      navigate('/merci-contact');
    } catch (err) {
      console.error('Contact form error:', err);
      setErrors({ submit: 'Une erreur est survenue. Veuillez réessayer.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#E9DFCF' }} data-testid="contact-page">
      <Header />

      <main className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Left Column - Contact Info */}
            <div className="lg:col-span-1">
              <h1
                className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900 mb-4"
                style={{ letterSpacing: '-0.04em' }}
              >
                Contact
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Décrivez votre besoin. Réponse sous 24–48h ouvrées.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#D8C9B3' }}>
                    <Mail className="w-5 h-5" style={{ color: '#1C2E4A' }} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Email</p>
                    <a href="mailto:contact@acdc-formation.com" className="text-gray-600 hover:underline">
                      contact@acdc-formation.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#D8C9B3' }}>
                    <Phone className="w-5 h-5" style={{ color: '#1C2E4A' }} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Téléphone</p>
                    <a href="tel:0678269110" className="text-gray-600 hover:underline">
                      06 78 26 91 10
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#D8C9B3' }}>
                    <MapPin className="w-5 h-5" style={{ color: '#1C2E4A' }} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Adresse</p>
                    <p className="text-gray-600">
                      7 avenue Paul Cézanne<br />
                      83600 Cogolin
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="lg:col-span-2">
              {/* Quiz Score Summary */}
              {quizScore && (
                <div
                  className="mb-6 p-4 rounded-xl border-2"
                  style={{ backgroundColor: '#1C2E4A', borderColor: '#1C2E4A' }}
                  data-testid="quiz-score-summary"
                >
                  <p className="text-white font-semibold text-lg">
                    Votre score : {quizScore}/100 – Niveau : {quizLevel}
                  </p>
                </div>
              )}

              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-200">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name fields */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="first_name" className="text-gray-700 font-medium">
                        Prénom *
                      </Label>
                      <Input
                        id="first_name"
                        value={formData.first_name}
                        onChange={(e) => handleChange('first_name', e.target.value)}
                        placeholder="Jean"
                        className={`mt-2 ${errors.first_name ? 'border-red-500' : ''}`}
                        data-testid="contact-first-name"
                      />
                      {errors.first_name && (
                        <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="last_name" className="text-gray-700 font-medium">
                        Nom *
                      </Label>
                      <Input
                        id="last_name"
                        value={formData.last_name}
                        onChange={(e) => handleChange('last_name', e.target.value)}
                        placeholder="Dupont"
                        className={`mt-2 ${errors.last_name ? 'border-red-500' : ''}`}
                        data-testid="contact-last-name"
                      />
                      {errors.last_name && (
                        <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <Label htmlFor="email" className="text-gray-700 font-medium">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder="jean.dupont@email.fr"
                      className={`mt-2 ${errors.email ? 'border-red-500' : ''}`}
                      data-testid="contact-email"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                    )}
                  </div>

                  {/* Phone & Company */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone" className="text-gray-700 font-medium">
                        Téléphone
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        placeholder="06 12 34 56 78"
                        className="mt-2"
                        data-testid="contact-phone"
                      />
                    </div>

                    <div>
                      <Label htmlFor="company" className="text-gray-700 font-medium">
                        Société
                      </Label>
                      <Input
                        id="company"
                        value={formData.company}
                        onChange={(e) => handleChange('company', e.target.value)}
                        placeholder="Nom de votre entreprise"
                        className="mt-2"
                        data-testid="contact-company"
                      />
                    </div>
                  </div>

                  {/* Sector */}
                  <div>
                    <Label htmlFor="sector" className="text-gray-700 font-medium">
                      Secteur d'activité
                    </Label>
                    <Select value={formData.sector} onValueChange={(value) => handleChange('sector', value)}>
                      <SelectTrigger id="sector" className="mt-2" data-testid="contact-sector">
                        <SelectValue placeholder="Sélectionnez votre secteur" />
                      </SelectTrigger>
                      <SelectContent>
                        {sectors.map((sector) => (
                          <SelectItem key={sector.value} value={sector.value}>
                            {sector.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Message */}
                  <div>
                    <Label htmlFor="message" className="text-gray-700 font-medium">
                      Votre message *
                    </Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      placeholder="Décrivez votre projet, vos besoins ou vos questions..."
                      className={`mt-2 min-h-[150px] ${errors.message ? 'border-red-500' : ''}`}
                      data-testid="contact-message"
                    />
                    {errors.message && (
                      <p className="mt-1 text-sm text-red-600">{errors.message}</p>
                    )}
                  </div>

                  {/* Contact Preference */}
                  <div>
                    <Label className="text-gray-700 font-medium mb-3 block">
                      Préférence de contact
                    </Label>
                    <RadioGroup
                      value={formData.contact_preference}
                      onValueChange={(value) => handleChange('contact_preference', value)}
                      className="flex gap-6"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="email" id="pref-email" data-testid="contact-pref-email" />
                        <Label htmlFor="pref-email" className="font-normal cursor-pointer">Email</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="phone" id="pref-phone" data-testid="contact-pref-phone" />
                        <Label htmlFor="pref-phone" className="font-normal cursor-pointer">Téléphone</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* RGPD Consent */}
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="rgpd_consent"
                      checked={formData.rgpd_consent}
                      onCheckedChange={(checked) => handleChange('rgpd_consent', checked)}
                      className={errors.rgpd_consent ? 'border-red-500' : ''}
                      data-testid="contact-rgpd"
                    />
                    <div>
                      <Label htmlFor="rgpd_consent" className="font-normal cursor-pointer text-gray-700">
                        J'accepte d'être recontacté dans le cadre de ma demande. *
                      </Label>
                      {errors.rgpd_consent && (
                        <p className="mt-1 text-sm text-red-600">{errors.rgpd_consent}</p>
                      )}
                    </div>
                  </div>

                  {/* Submit Error */}
                  {errors.submit && (
                    <div className="p-4 bg-red-50 text-red-700 rounded-lg">
                      {errors.submit}
                    </div>
                  )}

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full text-white rounded-full py-4 text-lg font-semibold transition-all duration-300 hover:-translate-y-0.5"
                    style={{ backgroundColor: '#1C2E4A' }}
                    data-testid="contact-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Envoyer mon message
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-gray-500 text-center">
                    Vos données sont traitées conformément à notre{' '}
                    <a href="/confidentialite" className="hover:underline" style={{ color: '#1C2E4A' }}>
                      politique de confidentialité
                    </a>.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
